/**
 * Builds fuel-sales table rows with product-wise subtotals and running outstanding,
 * mirroring the Blade daily transaction sheet grouping logic.
 *
 * Recoup is tank/shift-level (not nozzle-wise), so it is shown on a separate row.
 */

import { formatReportDate } from './reportFormat';

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export { formatReportDate };

const formatHoursMinutes = (hours, minutes) => {
  if (!Number.isFinite(hours) || hours < 0 || hours > 23) {
    return '';
  }

  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${suffix}`;
};

/**
 * Display shift / nozzle times as "h:mm AM".
 * Never returns the literal "Invalid date" (moment's output for null/empty/unparseable).
 * Time-only strings are formatted without Date() so timezone does not shift them.
 */
export const formatReportTime = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'object') {
    if (typeof value.isValid === 'function' && typeof value.format === 'function') {
      return value.isValid() ? value.format('h:mm A') : '';
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? ''
        : value.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
  }

  const str = String(value).trim();
  if (!str || /^invalid date$/i.test(str)) {
    return '';
  }

  if (/am|pm/i.test(str)) {
    const already = str.match(/(\d{1,2}:\d{2}(?::\d{2})?\s*[ap]m)/i);
    return already ? already[1].replace(/\s+/g, ' ') : str.replace(/\s+/g, ' ');
  }

  // Time-only, or the clock part of ISO / operational datetimes (Y-m-d or d-m-Y).
  const timeMatch = str.match(/(?:^|[T\s])(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (timeMatch) {
    return formatHoursMinutes(Number(timeMatch[1]), timeMatch[2]);
  }

  return '';
};

function createEmptyGroupTotals() {
  return {
    quantitySum: 0,
    recoupQtySum: 0,
    netQtySum: 0,
    debitTotal: 0,
    recoupAmountTotal: 0,
    outstanding: 0,
  };
}

function pushFuelSubtotal(bodyRows, totals) {
  bodyRows.push({
    type: 'subtotal',
    ...totals,
  });
}

function isRecoupDisplayRow(row) {
  return Boolean(row?.is_recoup_row)
    || String(row?.nozzle_name || '').localeCompare('Recoup', undefined, { sensitivity: 'accent' }) === 0;
}

/**
 * Keep nozzle sale rows free of recoup, and append one shift-level Recoup row.
 */
export function expandFuelSalesWithSeparateRecoupRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const groups = new Map();
  rows.forEach((row) => {
    const key = [
      row.tank_id ?? row.tank_name ?? '',
      row.op_date ?? '',
      row.op_emp_id ?? '',
    ].join('\0');
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(row);
  });

  const expanded = [];
  groups.forEach((group) => {
    if (group.some(isRecoupDisplayRow)) {
      expanded.push(...group.map((row) => ({ ...row })));
      return;
    }

    const totalRecoupQty = toNumber(group[0].recoup_qty);
    const totalRecoupAmount = toNumber(group[0].recoup_amount);

    group.forEach((row) => {
      expanded.push({
        ...row,
        recoup_qty: 0,
        recoup_amount: 0,
        is_recoup_row: false,
      });
    });

    if (Math.abs(totalRecoupQty) < 0.0005 && Math.abs(totalRecoupAmount) < 0.005) {
      return;
    }

    const first = group[0];
    expanded.push({
      ...first,
      nozzle_name: 'Recoup',
      opening_reading: '',
      close_reading: '',
      quantity: 0,
      amount: 0,
      dr_amount: 0,
      start_from: '',
      end_from: '',
      recoup_qty: Number(totalRecoupQty.toFixed(3)),
      recoup_amount: Number(totalRecoupAmount.toFixed(2)),
      is_recoup_row: true,
    });
  });

  return expanded;
}

/**
 * @param {Array<object>|null|undefined} rows
 * @param {number|string|null|undefined} openingBalance
 * @returns {{ bodyRows: Array<object>, lastGroupDebitTotal: number }}
 */
export function buildFuelSalesTableRows(rows, openingBalance = 0) {
  const expandedRows = expandFuelSalesWithSeparateRecoupRows(rows);
  if (expandedRows.length === 0) {
    return { bodyRows: [], lastGroupDebitTotal: 0 };
  }

  const bodyRows = [];
  let fuelBuffer = null;
  let groupTotals = createEmptyGroupTotals();
  let runningOutstanding = toNumber(openingBalance);
  let lastGroupDebitTotal = 0;

  expandedRows.forEach((row, index) => {
    const fuelName = row.fuel_name;
    const isRecoupRow = isRecoupDisplayRow(row);
    const recoupQty = toNumber(row.recoup_qty);
    const recoupAmount = toNumber(row.recoup_amount);
    const debitAmount = toNumber(row.dr_amount ?? row.amount);
    const quantity = toNumber(row.quantity);
    const netSales = debitAmount - recoupAmount;
    const netQty = quantity - recoupQty;

    if (fuelBuffer !== null && fuelBuffer !== fuelName) {
      pushFuelSubtotal(bodyRows, groupTotals);
      lastGroupDebitTotal = groupTotals.debitTotal;
      groupTotals = createEmptyGroupTotals();
    }

    fuelBuffer = fuelName;
    groupTotals.quantitySum += quantity;
    groupTotals.recoupQtySum += recoupQty;
    groupTotals.netQtySum += netQty;
    groupTotals.debitTotal += debitAmount;
    groupTotals.recoupAmountTotal += recoupAmount;
    runningOutstanding += netSales;
    groupTotals.outstanding = runningOutstanding;

    bodyRows.push({
      type: 'data',
      isRecoupRow,
      key: `${row.op_date || ''}-${row.nozzle_name || ''}-${row.opening_reading || ''}-${row.close_reading || ''}-${index}`,
      opDate: formatReportDate(row.op_date),
      fuelName: row.fuel_name,
      empName: row.emp_name || row.name || '',
      nozzleName: row.nozzle_name,
      startFrom: isRecoupRow ? '' : formatReportTime(row.start_from),
      endFrom: isRecoupRow ? '' : formatReportTime(row.end_from),
      openingReading: isRecoupRow ? '' : row.opening_reading,
      closeReading: isRecoupRow ? '' : row.close_reading,
      quantity: isRecoupRow ? '' : row.quantity,
      recoupQty,
      netQty: isRecoupRow ? -recoupQty : netQty,
      price: isRecoupRow ? '' : row.price,
      debitAmount: isRecoupRow ? '' : debitAmount,
      recoupAmount,
      netSales,
      outstanding: runningOutstanding,
    });
  });

  if (fuelBuffer !== null) {
    pushFuelSubtotal(bodyRows, groupTotals);
    lastGroupDebitTotal = groupTotals.debitTotal;
  }

  return { bodyRows, lastGroupDebitTotal };
}

/**
 * Nozzle-wise fuel sales summary with shift recoup on a separate row and running outstanding.
 */
export function buildFuelSalesNozzleSummaryFromDetailed(rows, openingBalance = 0) {
  let running = toNumber(openingBalance);

  return expandFuelSalesWithSeparateRecoupRows(rows || []).map((row, index) => {
    const quantity = toNumber(row.quantity);
    const drAmount = toNumber(row.dr_amount ?? row.amount);
    const recoupQty = toNumber(row.recoup_qty);
    const recoupAmount = toNumber(row.recoup_amount);
    const netAmount = drAmount - recoupAmount;
    running += netAmount;

    return {
      key: `fuel-summary-${index}`,
      fuel_name: row.fuel_name || '',
      nozzle_name: row.nozzle_name || '',
      quantity,
      recoup_qty: recoupQty,
      net_quantity: Number((quantity - recoupQty).toFixed(3)),
      price: row.price,
      dr_amount: drAmount,
      amount: drAmount,
      recoup_amount: recoupAmount,
      net_amount: Number(netAmount.toFixed(2)),
      outstanding: Number(running.toFixed(2)),
      is_recoup_row: isRecoupDisplayRow(row),
    };
  });
}
